// src/padplanner/LandownerPins.js
//
// The landowner's findings on the plan page's map (S6b2): a violet square
// with the finding's number at each finding's place, ringed when selected.
// Kept apart from the plan's own comment pins (CommentPins.js: orange discs)
// by shape and color as well as by layer, because a finding is the
// landowner's, read-only, and not part of the internal review. DOM buttons in
// an overlay over the map, as CommentPins' are, placed again on every move.
//
// The number is the portal's (portalFindingNumber.js: the markup's ordinal),
// so pin 3 here is finding 3 in the landowner's own portal.

/** Where a finding's pin sits: a point, a line's middle vertex, a ring's first vertex (CommentPins' rule). */
export function pinAt(g) {
  if (!g?.coordinates) return null;
  if (g.type === "Point") return g.coordinates;
  if (g.type === "LineString") return g.coordinates[Math.floor(g.coordinates.length / 2)] ?? null;
  if (g.type === "Polygon") return g.coordinates[0]?.[0] ?? null;
  if (g.type === "MultiPolygon") return g.coordinates[0]?.[0]?.[0] ?? null;
  if (g.type === "MultiLineString") return pinAt({ type: "LineString", coordinates: g.coordinates[0] ?? [] });
  if (g.type === "MultiPoint") return g.coordinates[0] ?? null;
  return null;
}

export default class LandownerPins {
  constructor({ map, onPick = null }) {
    this.map = map;
    this.onPick = onPick;
    this.pins = [];
    this.selectedId = null;
    this.visible = true;
    const container = map.getContainer?.();
    if (container) {
      const el = document.createElement("div");
      el.setAttribute("data-testid", "pp-landowner-pins");
      Object.assign(el.style, { position: "absolute", inset: "0", pointerEvents: "none", overflow: "hidden" });
      container.appendChild(el);
      this.el = el;
    }
    this._onMove = () => this._place();
    map.on("move", this._onMove);
  }

  destroy() {
    this.map.off?.("move", this._onMove);
    this.el?.remove();
    this.el = null;
  }

  /** pins: [{id, n, lngLat: [lng, lat], title}] — a finding with nowhere to sit is left out by the page. */
  set(pins, { selectedId = this.selectedId } = {}) {
    this.pins = pins ?? [];
    this.selectedId = selectedId;
    this._render();
  }

  setVisible(on) { this.visible = Boolean(on); this._render(); }

  _render() {
    const el = this.el;
    if (!el) return;
    el.replaceChildren();
    if (!this.visible) return;
    for (const p of this.pins) {
      if (!p.lngLat) continue;
      const b = document.createElement("button");
      b.type = "button";
      b.className = "pp-landowner-pin";
      b.dataset.findingId = p.id;
      b.textContent = p.n == null ? "•" : String(p.n);
      b.title = p.title ?? "";
      b.setAttribute("aria-label", `Landowner finding ${p.n ?? ""}`.trim());
      const selected = p.id === this.selectedId;
      b.dataset.selected = selected ? "true" : "false";
      Object.assign(b.style, {
        position: "absolute", left: "0", top: "0", width: "22px", height: "22px", borderRadius: "4px",
        pointerEvents: "auto", cursor: "pointer", font: "700 11px/18px ui-sans-serif, system-ui, sans-serif", textAlign: "center",
        color: "#ffffff", background: "#7c3aed",
        border: selected ? "2px solid #22d3ee" : "2px solid #ede9fe", padding: "0",
      });
      b._lngLat = p.lngLat;
      b.addEventListener("click", (e) => { e.stopPropagation(); this.onPick?.(p.id); });
      el.appendChild(b);
    }
    this._place();
  }

  _place() {
    if (!this.el) return;
    for (const n of this.el.children) {
      const p = this.map.project(n._lngLat);
      n.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)`;
    }
  }

  /** What the pins say now, for tests: [{id, n, selected}]. */
  shown() {
    return [...(this.el?.querySelectorAll(".pp-landowner-pin") ?? [])].map((b) => ({
      id: b.dataset.findingId, n: b.textContent, selected: b.dataset.selected === "true",
    }));
  }
}
